import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { BarChart3, Shield, EyeOff, Clock, CheckCircle2, ArrowRight, Download, FileText, Bell } from 'lucide-react';
const fadeIn = {
  initial: {
    opacity: 0,
    y: 20
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true
  },
  transition: {
    duration: 0.6
  }
};
const deals = [{
  id: 1,
  title: '3BR/3.5BA Marina Condo',
  price: '$300,000',
  revenue: '$60,000/yr',
  metricLabel: 'Cash-on-Cash',
  metricValue: '10%',
  category: 'Cash Flow'
}, {
  id: 2,
  title: '4BR Villa Punta Ballena',
  price: '$2,000,000',
  revenue: 'Reno: $500K',
  metricLabel: 'Post-Reno Value',
  metricValue: '$4.0M',
  category: 'Equity Play'
}, {
  id: 3,
  title: '0.5 Acre Pacific Lot',
  price: '$180,000',
  revenue: 'Comp Sales',
  metricLabel: 'Est. Value',
  metricValue: '$350K+',
  category: 'Vacant Land'
}, {
  id: 4,
  title: 'Commercial Retail Space Downtown',
  price: '$450,000',
  revenue: 'Est. NOI: $72K',
  metricLabel: 'Cap Rate',
  metricValue: '16%',
  category: 'Commercial'
}, {
  id: 5,
  title: '2BR Pre-Construction Corridor',
  price: '$275,000',
  revenue: 'Completion: Q4 2024',
  metricLabel: 'Est. Value',
  metricValue: '$425K',
  category: 'Pre-Construction'
}, {
  id: 6,
  title: '5BR Pedregal Fixer',
  price: '$1,500,000',
  revenue: 'Reno: $300K',
  metricLabel: 'ARV',
  metricValue: '$2.8M',
  category: 'Equity Play'
}, {
  id: 7,
  title: 'Studio Medano Beach',
  price: '$195,000',
  revenue: '$38,000/yr',
  metricLabel: 'Cash-on-Cash',
  metricValue: '12%',
  category: 'Cash Flow'
}, {
  id: 8,
  title: '1 Acre East Cape Beachfront',
  price: '$320,000',
  revenue: 'Zoning: Hotel',
  metricLabel: 'Development',
  metricValue: 'High',
  category: 'Vacant Land'
}];
const categories = ['All', 'Cash Flow', 'Equity Play', 'Vacant Land', 'Commercial', 'Pre-Construction'];
export function DealFlowHome() {
  const [activeCategory, setActiveCategory] = useState('All');
  const [surveyStep, setSurveyStep] = useState(1);
  const [investorId, setInvestorId] = useState('');
  const [surveyData, setSurveyData] = useState({
    dealType: '',
    budget: '',
    timeline: '',
    experience: '',
    priorities: [] as string[],
    area: '',
    extra: '',
    email: ''
  });
  const filteredDeals = activeCategory === 'All' ? deals : deals.filter(d => d.category === activeCategory);
  const handlePriorityToggle = (priority: string) => {
    setSurveyData(prev => ({
      ...prev,
      priorities: prev.priorities.includes(priority) ? prev.priorities.filter(p => p !== priority) : [...prev.priorities, priority]
    }));
  };
  const submitSurvey = (e: React.FormEvent) => {
    e.preventDefault();
    // Generate random 8 char ID
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = 'CBF-';
    for (let i = 0; i < 4; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setInvestorId(result);
    setSurveyStep(2);
    console.log('Survey Data Submitted:', surveyData);
  };
  return <div className="w-full bg-charcoal-dark text-white" data-id="element-162">
      {/* SECTION A: HERO */}
      <section id="hero" className="relative h-screen flex flex-col justify-center items-center overflow-hidden" data-id="element-163">
        <div className="absolute inset-0 z-0" data-id="element-164">
          <img src="https://images.unsplash.com/photo-1512100356356-de1b84283e18?auto=format&fit=crop&q=80" alt="Aerial Cabo San Lucas" className="w-full h-full object-cover" data-id="element-165" />
          <div className="absolute inset-0 bg-charcoal-dark/90 mix-blend-multiply" data-id="element-166" />
          <div className="absolute inset-0 bg-gradient-to-b from-charcoal-dark/50 via-transparent to-charcoal-dark" data-id="element-167" />
        </div>

        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto mt-20" data-id="element-168">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} className="text-5xl md:text-7xl font-editorial text-white mb-6 leading-tight tracking-wide" data-id="element-169">
            Curated Real Estate <br data-id="element-170" />
            <span className="text-gold" data-id="element-171">Deal Flow.</span> <br data-id="element-172" />
            Cabo San Lucas.
          </motion.h1>
          <motion.p initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="text-lg text-slate-light font-light mb-10 max-w-3xl mx-auto leading-relaxed" data-id="element-173">
            Cabo Deal Flow is a curated, independently analyzed real estate
            investment resource. Every deal listed here has been vetted,
            researched, and backed by quantified data — rental yield, cash flow,
            ROI, equity upside. This is not a listing site. This is deal flow.
          </motion.p>
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.4
        }} data-id="element-174">
            <a href="#deal-report" className="inline-block bg-gold text-charcoal-dark px-8 py-4 uppercase tracking-widest text-sm font-semibold hover:bg-white transition-colors" data-id="element-175">
              Access the Deal Report
            </a>
          </motion.div>
        </div>

        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 1
      }} className="absolute bottom-0 left-0 w-full border-t border-charcoal-light bg-charcoal-dark/80 backdrop-blur-md py-4" data-id="element-176">
          <div className="max-w-7xl mx-auto px-4 flex flex-wrap justify-center gap-8 md:gap-16 text-sm tracking-widest uppercase text-slate-light" data-id="element-177">
            <span data-id="element-178">
              <strong className="text-white" data-id="element-179">12</strong> Active Deals
            </span>
            <span data-id="element-180">
              <strong className="text-white" data-id="element-181">34</strong> Completed
            </span>
            <span data-id="element-182">
              <strong className="text-white" data-id="element-183">$47M</strong> Total Volume
            </span>
          </div>
        </motion.div>
      </section>

      {/* SECTION B: WHAT IS CABO DEAL FLOW */}
      <section id="overview" className="py-24 bg-white text-charcoal-dark" data-id="element-184">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-185">
          <motion.div {...fadeIn} className="text-center mb-16" data-id="element-186">
            <h2 className="text-3xl md:text-4xl font-editorial mb-4" data-id="element-187">
              The Philosophy
            </h2>
            <div className="w-12 h-1 bg-gold mx-auto" data-id="element-188"></div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-189">
            {[{
            icon: Shield,
            title: 'Curated, Not Listed',
            desc: 'Not everything makes the cut. Every deal passes rigorous financial analysis before being presented.'
          }, {
            icon: BarChart3,
            title: 'Data Is the Language',
            desc: 'Cash flow %, ROI, equity multiples, cap rates. No fluff, just the numbers that matter.'
          }, {
            icon: EyeOff,
            title: 'Privacy First',
            desc: "We don't ask for your name or phone number. We have no reason to contact you unless you initiate."
          }, {
            icon: Clock,
            title: "When You're Ready",
            desc: "The quality of our deals speaks for itself. You'll contact us when you see something worth your time."
          }].map((item, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="p-8 border border-slate-light/30 hover:border-gold transition-colors" data-id="element-190">
                <item.icon className="text-gold mb-6" size={32} strokeWidth={1.5} data-id="element-191" />
                <h3 className="text-xl font-editorial mb-3" data-id="element-192">{item.title}</h3>
                <p className="text-slate text-sm leading-relaxed" data-id="element-193">
                  {item.desc}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* SECTION C: CURRENT DEALS */}
      <section id="current-deals" className="py-24 bg-charcoal-dark" data-id="element-194">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-195">
          <motion.div {...fadeIn} className="mb-12" data-id="element-196">
            <h2 className="text-3xl md:text-4xl font-editorial text-white mb-8" data-id="element-197">
              Current Deal Flow
            </h2>

            {/* Filters */}
            <div className="flex flex-wrap gap-3" data-id="element-198">
              {categories.map(cat => <button key={cat} onClick={() => setActiveCategory(cat)} className={`px-4 py-2 text-xs uppercase tracking-widest transition-colors border ${activeCategory === cat ? 'bg-gold border-gold text-charcoal-dark font-semibold' : 'border-charcoal-light text-slate-light hover:border-slate'}`} data-id="element-199">
                  {cat}
                </button>)}
            </div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-200">
            {filteredDeals.map((deal, i) => <motion.div key={deal.id} initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.3
          }} className="bg-charcoal border border-charcoal-light p-6 hover:border-gold/50 transition-colors flex flex-col" data-id="element-201">
                <div className="flex justify-between items-start mb-4" data-id="element-202">
                  <span className="text-[10px] uppercase tracking-widest text-gold border border-gold/30 px-2 py-1" data-id="element-203">
                    {deal.category}
                  </span>
                </div>
                <h3 className="text-xl font-editorial text-white mb-6 flex-grow" data-id="element-204">
                  {deal.title}
                </h3>

                <div className="space-y-4 mb-8" data-id="element-205">
                  <div className="flex justify-between border-b border-charcoal-light pb-2" data-id="element-206">
                    <span className="text-slate-light text-sm" data-id="element-207">Price</span>
                    <span className="text-white font-medium" data-id="element-208">{deal.price}</span>
                  </div>
                  <div className="flex justify-between border-b border-charcoal-light pb-2" data-id="element-209">
                    <span className="text-slate-light text-sm" data-id="element-210">
                      Est. Revenue
                    </span>
                    <span className="text-white font-medium" data-id="element-211">
                      {deal.revenue}
                    </span>
                  </div>
                  <div className="flex justify-between border-b border-charcoal-light pb-2" data-id="element-212">
                    <span className="text-slate-light text-sm" data-id="element-213">
                      {deal.metricLabel}
                    </span>
                    <span className="text-gold font-semibold" data-id="element-214">
                      {deal.metricValue}
                    </span>
                  </div>
                </div>

                <a href="#deal-report" className="text-xs uppercase tracking-widest text-slate-light hover:text-gold flex items-center group" data-id="element-215">
                  Full Analysis in Deal Report{' '}
                  <ArrowRight size={14} className="ml-2 group-hover:translate-x-1 transition-transform" data-id="element-216" />
                </a>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* SECTION D: COMPLETED DEALS */}
      <section id="completed-deals" className="py-24 bg-sand text-charcoal-dark" data-id="element-217">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-218">
          <motion.div {...fadeIn} className="mb-16 text-center" data-id="element-219">
            <h2 className="text-3xl md:text-4xl font-editorial mb-4" data-id="element-220">
              Completed Deals — Track Record
            </h2>
            <p className="text-slate max-w-2xl mx-auto" data-id="element-221">
              Verified outcomes from previous deal flow recommendations.
            </p>
          </motion.div>

          <div className="space-y-4 max-w-4xl mx-auto" data-id="element-222">
            {['2BR Marina Condo — Purchased $220K (2022). Current annual cash flow: 12%. Fully rented 48 weeks/year.', 'Vacant Lot East Cape — Purchased $95K (2021). Current market value: $210K. 121% appreciation.', '3BR Corridor Villa — Purchased $1.2M, Renovated $400K. Sold $2.8M (2023). Net profit: $1.2M.', 'Commercial Storage Facility — Built for $350K. Annual NOI: $85K. 24% cash-on-cash return.', 'Pre-Construction 2BR — Purchased $180K (2021). Current value: $340K. 89% appreciation.'].map((deal, i) => <motion.div key={i} initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="bg-white p-6 border border-slate-light/20 flex items-start shadow-sm" data-id="element-223">
                <CheckCircle2 className="text-green-600 mr-4 shrink-0 mt-1" size={20} data-id="element-224" />
                <p className="text-charcoal-dark font-light leading-relaxed" data-id="element-225">
                  {deal}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* SECTION E: THE DEAL REPORT + SURVEY */}
      <section id="deal-report" className="py-24 bg-charcoal border-y border-charcoal-light" data-id="element-226">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-227">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16" data-id="element-228">
            {/* Left Side: Explanation */}
            <motion.div {...fadeIn} data-id="element-229">
              <h2 className="text-3xl md:text-5xl font-editorial text-white mb-6" data-id="element-230">
                Get the Full Cabo Deal Flow Report
              </h2>
              <div className="prose prose-invert prose-lg text-slate-light font-light mb-8" data-id="element-231">
                <p data-id="element-232">
                  This report contains complete investment analysis, financial
                  models, and ongoing deal rankings — updated as the market
                  shifts.
                </p>
                <p data-id="element-233">
                  To access it, complete a short investor survey (~3 minutes).
                  The survey helps us continuously improve deal quality. When we
                  understand what the market wants, we source better deals.
                </p>
              </div>
              <div className="bg-charcoal-dark p-6 border-l-4 border-gold" data-id="element-234">
                <p className="text-white font-medium mb-2" data-id="element-235">Privacy Guarantee</p>
                <p className="text-sm text-slate-light" data-id="element-236">
                  We do NOT ask for your name or phone number. This survey is
                  anonymous unless you choose to share contact info.
                </p>
              </div>
            </motion.div>

            {/* Right Side: Survey Form */}
            <motion.div {...fadeIn} className="bg-charcoal-dark border border-charcoal-light p-8 md:p-10" data-id="element-237">
              {surveyStep === 1 ? <form onSubmit={submitSurvey} className="space-y-8" data-id="element-238">
                  <div data-id="element-239">
                    <label className="block text-xs uppercase tracking-widest text-gold mb-4" data-id="element-240">
                      1. Primary Focus
                    </label>
                    <div className="space-y-2" data-id="element-241">
                      {['Cash Flow', 'Equity Appreciation', 'Land', 'Commercial', 'Open to All'].map(opt => <label key={opt} className="flex items-center space-x-3 cursor-pointer" data-id="element-242">
                          <input type="radio" name="dealType" required value={opt} onChange={e => setSurveyData({
                      ...surveyData,
                      dealType: e.target.value
                    })} className="accent-gold" data-id="element-243" />
                          <span className="text-sm text-slate-light" data-id="element-244">
                            {opt}
                          </span>
                        </label>)}
                    </div>
                  </div>

                  <div data-id="element-245">
                    <label className="block text-xs uppercase tracking-widest text-gold mb-4" data-id="element-246">
                      2. Budget Range
                    </label>
                    <div className="space-y-2" data-id="element-247">
                      {['$250K-$500K', '$500K-$1M', '$1M-$3M', '$3M+'].map(opt => <label key={opt} className="flex items-center space-x-3 cursor-pointer" data-id="element-248">
                            <input type="radio" name="budget" required value={opt} onChange={e => setSurveyData({
                      ...surveyData,
                      budget: e.target.value
                    })} className="accent-gold" data-id="element-249" />
                            <span className="text-sm text-slate-light" data-id="element-250">
                              {opt}
                            </span>
                          </label>)}
                    </div>
                  </div>

                  <div data-id="element-251">
                    <label className="block text-xs uppercase tracking-widest text-gold mb-4" data-id="element-252">
                      3. Investment Timeline
                    </label>
                    <div className="space-y-2" data-id="element-253">
                      {['Ready Now', '3-6 Months', '6-12 Months', '12+ Months'].map(opt => <label key={opt} className="flex items-center space-x-3 cursor-pointer" data-id="element-254">
                          <input type="radio" name="timeline" required value={opt} onChange={e => setSurveyData({
                      ...surveyData,
                      timeline: e.target.value
                    })} className="accent-gold" data-id="element-255" />
                          <span className="text-sm text-slate-light" data-id="element-256">
                            {opt}
                          </span>
                        </label>)}
                    </div>
                  </div>

                  <div data-id="element-257">
                    <label className="block text-xs uppercase tracking-widest text-gold mb-4" data-id="element-258">
                      4. First-Time Cabo Investor?
                    </label>
                    <div className="space-y-2" data-id="element-259">
                      {['Yes, first time', "I've invested in Cabo before"].map(opt => <label key={opt} className="flex items-center space-x-3 cursor-pointer" data-id="element-260">
                            <input type="radio" name="experience" required value={opt} onChange={e => setSurveyData({
                      ...surveyData,
                      experience: e.target.value
                    })} className="accent-gold" data-id="element-261" />
                            <span className="text-sm text-slate-light" data-id="element-262">
                              {opt}
                            </span>
                          </label>)}
                    </div>
                  </div>

                  <div data-id="element-263">
                    <label className="block text-xs uppercase tracking-widest text-gold mb-4" data-id="element-264">
                      5. What matters most? (Select all)
                    </label>
                    <div className="grid grid-cols-2 gap-2" data-id="element-265">
                      {['Cash Flow', 'Equity Upside', 'Long-term Hold', 'Short-term Flip', 'Land Banking', 'Commercial'].map(opt => <label key={opt} className="flex items-center space-x-3 cursor-pointer" data-id="element-266">
                          <input type="checkbox" checked={surveyData.priorities.includes(opt)} onChange={() => handlePriorityToggle(opt)} className="accent-gold" data-id="element-267" />
                          <span className="text-sm text-slate-light" data-id="element-268">
                            {opt}
                          </span>
                        </label>)}
                    </div>
                  </div>

                  <div data-id="element-269">
                    <label className="block text-xs uppercase tracking-widest text-gold mb-4" data-id="element-270">
                      6. Preferred Area of Cabo
                    </label>
                    <div className="space-y-2" data-id="element-271">
                      {['Marina', 'Corridor', 'East Cape', 'Pedregal', 'Pacific Side', 'Open to All'].map(opt => <label key={opt} className="flex items-center space-x-3 cursor-pointer" data-id="element-272">
                          <input type="radio" name="area" required value={opt} onChange={e => setSurveyData({
                      ...surveyData,
                      area: e.target.value
                    })} className="accent-gold" data-id="element-273" />
                          <span className="text-sm text-slate-light" data-id="element-274">
                            {opt}
                          </span>
                        </label>)}
                    </div>
                  </div>

                  <div data-id="element-275">
                    <label className="block text-xs uppercase tracking-widest text-gold mb-4" data-id="element-276">
                      7. Anything Else? (Optional)
                    </label>
                    <textarea placeholder="Any additional context about what you're looking for..." value={surveyData.extra} onChange={e => setSurveyData({
                  ...surveyData,
                  extra: e.target.value
                })} rows={3} className="w-full bg-charcoal border border-charcoal-light px-4 py-3 text-white focus:outline-none focus:border-gold text-sm resize-none" data-id="element-277" />
                  </div>

                  <div data-id="element-278">
                    <label className="block text-xs uppercase tracking-widest text-gold mb-4" data-id="element-279">
                      Email (Optional)
                    </label>
                    <input type="email" placeholder="For report updates..." value={surveyData.email} onChange={e => setSurveyData({
                  ...surveyData,
                  email: e.target.value
                })} className="w-full bg-charcoal border border-charcoal-light px-4 py-3 text-white focus:outline-none focus:border-gold text-sm" data-id="element-280" />
                  </div>

                  <button type="submit" className="w-full bg-gold text-charcoal-dark py-4 uppercase tracking-widest text-sm font-semibold hover:bg-white transition-colors" data-id="element-281">
                    Generate Report & ID
                  </button>
                </form> : <div className="text-center py-10" data-id="element-282">
                  <div className="w-20 h-20 bg-charcoal border border-gold rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-283">
                    <FileText className="text-gold" size={32} data-id="element-284" />
                  </div>
                  <h3 className="text-sm uppercase tracking-widest text-slate-light mb-2" data-id="element-285">
                    Your Investor ID
                  </h3>
                  <div className="text-4xl font-editorial text-white mb-6 tracking-widest bg-charcoal py-4 border border-charcoal-light" data-id="element-286">
                    {investorId}
                  </div>
                  <p className="text-sm text-slate-light mb-8 leading-relaxed" data-id="element-287">
                    Save or screenshot your Investor ID. When you contact us,
                    reference this ID and we'll pull exactly what you told us —
                    no need to start over.
                  </p>
                  <button onClick={() => alert('Downloading Deal Report PDF...')} className="inline-flex items-center justify-center w-full bg-gold text-charcoal-dark py-4 uppercase tracking-widest text-sm font-semibold hover:bg-white transition-colors" data-id="element-288">
                    <Download className="mr-2" size={18} data-id="element-289" /> Download Deal Report
                  </button>
                </div>}
            </motion.div>
          </div>
        </div>
      </section>

      {/* SECTION F: HOW IT WORKS */}
      <section className="py-24 bg-white text-charcoal-dark" data-id="element-290">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-291">
          <h2 className="text-3xl font-editorial mb-16" data-id="element-292">How It Works</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 relative" data-id="element-293">
            <div className="hidden md:block absolute top-8 left-[16%] right-[16%] h-[1px] bg-slate-light/30 z-0" data-id="element-294"></div>

            {[{
            step: '01',
            title: 'Browse the Deal Flow',
            desc: 'See curated deals with real data and financial metrics.'
          }, {
            step: '02',
            title: 'Complete the Survey',
            desc: '3 minutes, anonymous, no contact info required.'
          }, {
            step: '03',
            title: 'Download & Save ID',
            desc: "Reference your ID when you're ready to talk."
          }].map((item, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.2
          }} className="relative z-10" data-id="element-295">
                <div className="w-16 h-16 bg-charcoal-dark text-gold rounded-full flex items-center justify-center mx-auto mb-6 text-xl font-editorial border-4 border-white" data-id="element-296">
                  {item.step}
                </div>
                <h3 className="text-xl font-editorial mb-3" data-id="element-297">{item.title}</h3>
                <p className="text-slate text-sm" data-id="element-298">{item.desc}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* SECTION G: DEAL ALERTS */}
      <section id="alerts" className="py-24 bg-charcoal-dark border-t border-charcoal-light text-center" data-id="element-299">
        <div className="max-w-3xl mx-auto px-4" data-id="element-300">
          <Bell className="text-gold mx-auto mb-6" size={32} data-id="element-301" />
          <h2 className="text-3xl font-editorial text-white mb-4" data-id="element-302">
            Join the Alert List
          </h2>
          <p className="text-slate-light mb-10" data-id="element-303">
            Tell us what you're looking for. When a matching deal hits our
            radar, we'll reach out — only then. No spam. No sales calls. Deals
            only.
          </p>
          <form className="flex flex-col sm:flex-row gap-4 max-w-xl mx-auto" onSubmit={e => {
          e.preventDefault();
          alert('Subscribed to alerts.');
        }} data-id="element-304">
            <input type="email" required placeholder="Email Address" className="flex-grow bg-charcoal border border-charcoal-light px-4 py-3 text-white focus:outline-none focus:border-gold text-sm" data-id="element-305" />
            <button type="submit" className="bg-white text-charcoal-dark px-8 py-3 uppercase tracking-widest text-sm font-semibold hover:bg-gold transition-colors whitespace-nowrap" data-id="element-306">
              Get Alerts
            </button>
          </form>
        </div>
      </section>

      {/* SECTION H: ABOUT */}
      <section id="about" className="py-24 bg-charcoal border-t border-charcoal-light" data-id="element-307">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-308">
          <div className="flex flex-col md:flex-row gap-12 items-center" data-id="element-309">
            <div className="w-48 h-48 shrink-0 rounded-full overflow-hidden border-2 border-gold/30" data-id="element-310">
              <img src="https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&q=80" alt="Trevor Williams" className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-500" data-id="element-311" />
            </div>
            <div data-id="element-312">
              <h4 className="text-gold text-xs uppercase tracking-widest mb-2" data-id="element-313">
                Founder
              </h4>
              <h2 className="text-3xl font-editorial text-white mb-4" data-id="element-314">
                Trevor Williams
              </h2>
              <p className="text-slate-light font-light leading-relaxed mb-6" data-id="element-315">
                With over a decade of experience in Los Cabos real estate,
                Trevor built Cabo Deal Flow to solve a specific problem: the
                lack of institutional-grade data in the luxury vacation market.
              </p>
              <p className="text-slate-light font-light leading-relaxed" data-id="element-316">
                His philosophy is simple: be data-driven, privacy-first, and
                investor-first. By focusing on the numbers rather than the hype,
                he helps family offices and high-net-worth individuals secure
                assets that perform.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>;
}